<template>
	<main class="page">
		<div class="startsida">
			<div class="flex">
				<div class="spalt content" id="startvy">

					<h1 class="title">{{ $t("main.title")}}</h1>
					<p>{{ $t("general.info_texts.here_you_announce_first_part") }}</p>
					<p>{{ $t("general.info_texts.here_you_announce_second_part") }}</p>

					<div class="errormsg" tabindex="-1" role="alert"
						v-if="visaFelmeddelande()">
						<img class="errormsg_symbol" width="32" height="32"
							src="../../public/images/error.svg" alt="" />
						<p>{{ store.state.felmeddelande }}</p>
					</div>
					<div aria-live="polite" :aria-busy="visaLaddarSymbol() ? 'true' : 'false'">
						<div v-if="visaLaddarSymbol()" class="laddar-container">
							<LaddarSymbol/>
						</div>
						<div v-else>
							<Form @submit="onSubmit" :validation-schema="schema"
								@invalid-submit="onInvalidSubmit" v-slot="{ errors }">

								<div class="errormsg" role="alert"
									v-if="Object.keys(errors).length != 0" v-run="rensaBackendError()">
									<img class="errormsg_symbol" width="32" height="32"
										src="../../public/images/error.svg" alt="" />
									<div class="errormsg_content">
										<h2 tabindex="-1">
											<template v-if="Object.keys(errors).length == 1">{{ $t("general.errors.correct_issue") }}</template>
											<template v-else>{{ $t("general.errors.correct_issues", { count: Object.keys(errors).length.toString()}) }}</template>
										</h2>
										<ul>
											<li v-if="errors.orgnr"><a class="globalMessage" href="#orgnummerFalt">{{ errors.orgnr }}</a></li>
											<li v-if="errors.kod"><a class="globalMessage" href="#kodFalt">{{ errors.kod }}</a></li>
										</ul>
									</div>
								</div>
								<div class="form-container">
									<div class="form-row">
										<TextInput 
											id="orgnummerFalt" 
											name="orgnr" 
											type="text" 
											:required="true" 
											label="Organisationsnummer"
											mode="passive"
											arialabel="Organisationsnummer, skriv i formatet 6 siffror bindestreck 4 siffror." 
											helper="Format: XXXXXX-XXXX" 
											:required-pattern="orgnummerRegex" 
											:default-value="store.state.orgnummer"
											/>
									</div>
									<div class="form-row">
										<TextInput 
											id="kodFalt" 
											name="kod" 
											type="text" 
											:required="true" 
											label="Kod"
											mode="passive"
											arialabel="Kod, skriv in koden som kom i brevet" 
											autocomplete="off" 
											:default-value="store.state.kod"
											/>
									</div>
								</div>
								<div class="button-container">
									<button aria-label="Nästa steg" class="next"
										type="submit">Nästa</button>
								</div>
							</Form>
						</div>
					</div>
					
				</div>
				<div class="spalt intro"></div>
			</div>
		</div>
	</main>
</template>

<script setup lang="ts">
import { Form } from 'vee-validate';
import * as Yup from 'yup';
import TextInput from '@/components/partials/TextInput.vue';
import { useStore } from '@/store';
import { kontrolleraKod } from "@/api";
import router from '@/router';
import { orgnummerRegex } from '@/validation'
import LaddarSymbol from '@/components/partials/LaddarSymbol.vue';

const store = useStore();

const visaLaddarSymbol = (): boolean => {
	return store.state.vantarPaResponse;
}

const visaFelmeddelande = (): boolean => {
	return store.state.felmeddelande.length > 0 && !store.state.verifierad;
}

const onSubmit = () => {
	store.state.orgnummer = orgnummerFalt.value.trim();
	store.state.kod = kodFalt.value.trim();
	
	kontrolleraKod(() => {
		router.push("/svara");
	});
};

const rensaBackendError = () => {
	store.aterstall();
}

const onInvalidSubmit = () => {
	const firstEl = document.querySelector('input:invalid');
	if (firstEl) {
		firstEl.focus();
	}
}

import i18n from "../i18n";
const felmeddelandeOrgnummer: string = i18n.global.t("general.errors.fill_registration_number");
const felmeddelandeKod: string = i18n.global.t("general.errors.fill_code");

const schema = Yup.object().shape({
    orgnr: Yup
    .string()
	.trim()
    .required(felmeddelandeOrgnummer)
	.matches(new RegExp(orgnummerRegex), felmeddelandeOrgnummer),
	kod: Yup
    .string()
    .required(felmeddelandeKod)
});

</script>

<style lang="scss">
.laddar-container {
	display: grid;
  	place-items: center;
  	height: 10rem;
}

.flex {
	display: flex;
	flex-direction: row;
}

main {
	margin-top: 0;
	width: 100%;
	display: flex;
}

.startsida {
	width: 100%;
	flex: 1;
	display: flex;
	background-image: url("../../public/images/intro-mtelefon-s.jpg");
	background-repeat: no-repeat;
	background-position-y: center;
	background-position-x: 200px;
	background-size: cover;
}

.compl {
	font-size: 90%;
	color: #e2e2e2;
	display: inline-block;
	margin-top: 0.4rem;
}

.title {
	margin-bottom: 1rem;
}

.spalt {
	flex: 1;
	align-items: stretch;
}

.content {
	padding: 2rem 2rem 2rem 0;
	background-color: white;
}

.intro {
	padding: 2rem;
}

p {
	color: black;
}

.form-container {
	display: grid;
	gap: 1.5rem;
	margin: 1rem 0;
	padding: 1.5rem 1.5rem 1.8rem 1.5rem;
	background-color: $color-darkblue-bv;
}

.block {
	display: block;
	width: 100%;
}

.m-t-b {
	margin: 0.5rem 0;
}

.button-container {
	text-align: right;
}

button.next {
	display: inline-block;
	padding: 0.5em;
	font-weight: bold;
	border-radius: 0.2em;
	border: none;
	background: $color-darkblue-bv;
	color: #ffffff;
	transition: all 0.1s;
	cursor: pointer;
	text-shadow: none;
}

.form-row {
	display: flex;
	flex-direction: column;
}

.errormsg_content h2 {
	font-size: 1rem;
	margin: 0 0 0.2rem 0;
}

.errormsg_content li {
	list-style-type: none;
	margin-left: 0;
	padding-left: 0;
}
.errormsg p {
	margin-bottom:0;
}

@media screen and (max-width: 70em) {
	.intro {
		display: none;
	}
	.content {
		padding: 3rem 8rem;
	}
	.startsida {
		background-image: url("../../public/images/intro-mtelefon-s.jpg");
	}
}

@media screen and (max-width: 35em) {
	.intro {
		display: none;
	}
	.content {
		padding: 2rem;
	}
	.startsida {
		background-image: none;
	}
}
</style>
